import { useCallback, useEffect, useRef, useState } from 'react';
import { adminApi } from './api';
import UserManagement from './UserManagement';
import AdminOperators, { InvitationAcceptScreen } from './AdminOperators';
import ReferralManager from './ReferralManager';
import ResourceManager from './ResourceManager';
import CourseManager from './CourseManager';
import ModulesManager from './ModulesManager';
import QuizManager from './QuizManager';
import SimulationManager from './SimulationManager';
import SubscriptionPlanManager from './SubscriptionPlanManager';
import StatusToast from './StatusToast';
import './styles.css';
import './professional.css';

const NAV_ITEMS = [
  ['dashboard', 'Vue d’ensemble', '⌂', null],
  ['modules', 'Matières', 'M', 'content:manage'],
  ['quiz', 'Quiz', 'Q', 'content:manage'],
  ['courses', 'Cours', 'C', 'content:manage'],
  ['simulations', 'Simulations', 'S', 'content:manage'],
  ['resources', 'Ressources', 'R', 'content:manage'],
  ['users', 'Candidats', 'U', 'users:read'],
  ['billing', 'Abonnements', 'B', 'billing:manage'],
  ['referrals', 'Parrainage', 'P', 'referrals:manage'],
  ['operators', 'Administrateurs', 'A', 'admin:manage'],
];

// Lightweight native SVG icons: no added dependency and no change to navigation behavior.
function NavIcon({ name }) {
  const drawings = {
    dashboard: <><rect x="3" y="3" width="7" height="7" rx="1.3" /><rect x="14" y="3" width="7" height="7" rx="1.3" /><rect x="3" y="14" width="7" height="7" rx="1.3" /><rect x="14" y="14" width="7" height="7" rx="1.3" /></>,
    modules: <><path d="M4 5.5C7 4 10 4 12 5.5c2-1.5 5-1.5 8 0v14c-3-1.5-6-1.5-8 0-2-1.5-5-1.5-8 0z" /><path d="M12 5.5v14" /></>,
    quiz: <><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 8h8M8 12h4M8 16h8" /><path d="m15 11 1.5 1.5L19 10" /></>,
    courses: <><path d="M5 4h11a3 3 0 0 1 3 3v13H7a2 2 0 0 1-2-2z" /><path d="M5 17a3 3 0 0 1 3-3h11M9 7h6" /></>,
    simulations: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2.5M9 3h6" /></>,
    resources: <><path d="M3 7a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><path d="M7 12h10M7 16h7" /></>,
    referrals: <><circle cx="8" cy="7" r="3"/><circle cx="17" cy="10" r="3"/><path d="M2 20v-3a6 6 0 0 1 12 0v3M14 16a5 5 0 0 1 8 4" /></>,
    users: <><circle cx="9" cy="8" r="3" /><path d="M3.5 19v-2a5.5 5.5 0 0 1 11 0v2zM16 5.5a3 3 0 0 1 0 5.5M17 13.5a5 5 0 0 1 3.5 5" /></>,
    billing: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 9h18M7 14h4" /></>,
    operators: <><path d="m12 2 8 3v6c0 5.5-3.5 9-8 11-4.5-2-8-5.5-8-11V5z" /><path d="m9 12 2 2 4-4" /></>,
  };
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
      strokeLinecap="round" strokeLinejoin="round" focusable="false" aria-hidden="true">
      {drawings[name]}
    </svg>
  );
}

const MOBILE_MENU_QUERY = '(max-width: 900px)';

function isCompactViewport() {
  if (typeof window.matchMedia === 'function') {
    return window.matchMedia(MOBILE_MENU_QUERY).matches;
  }
  return window.innerWidth <= 900;
}

function hasPermission(admin, permission) {
  if (!permission) return true;
  const permissions = admin?.permissions || [];
  return permissions.includes('*') || permissions.includes(permission);
}

function LoginScreen({ onAuthenticated }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(event) {
    event.preventDefault();
    try {
      setBusy(true);
      setError('');
      const result = await adminApi.login(email, password);
      onAuthenticated(result.user);
    } catch (loginError) {
      setError(loginError.message || 'Connexion impossible.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="bo-login-page">
      <section className="bo-login-card">
        <div className="bo-brand-mark">CB</div>
        <p className="bo-eyebrow">CONCOURSBf</p>
        <h1>Administration</h1>
        <p className="bo-muted">
          Interface réservée aux gestionnaires autorisés de la plateforme.
        </p>

        <form onSubmit={submit} className="bo-form bo-login-form">
          <label>
            Adresse e-mail
            <input
              className="bo-input"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </label>
          <label>
            Mot de passe
            <input
              className="bo-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>
          {error && <div className="bo-alert bo-alert--error">{error}</div>}
          <button className="bo-button bo-button--primary" disabled={busy}>
            {busy ? 'Connexion…' : 'Se connecter'}
          </button>
        </form>
      </section>
    </main>
  );
}

function Dashboard({ catalog, users }) {
  const overview = catalog?.overview || {};
  const cards = [
    ['Matières', overview.modules ?? 0],
    ['Cours', overview.courses ?? 0],
    ['Sujets Quiz', overview.quiz_subjects ?? 0],
    ['Questions Quiz', overview.quiz_questions ?? 0],
    ['Ressources', overview.resources ?? 0],
    ['Lots simulation', overview.simulation_lots ?? 0],
    ['Sujets simulation', overview.simulations ?? 0],
    ['Contenus Premium', overview.premium_items ?? 0],
    ['Candidats', users?.total ?? users?.items?.length ?? 0],
  ];

  const draftCount =
    Number(overview.course_drafts || 0) +
    (catalog?.quizSubjects || []).filter((item) => item.publication_status === 'draft').length +
    Number(overview.resource_drafts || 0) +
    Number(overview.simulation_lot_drafts || 0) +
    Number(overview.simulation_drafts || 0);

  return (
    <div className="bo-stack">
      <div>
        <p className="bo-eyebrow">Tableau de bord</p>
        <h2>Vue d’ensemble</h2>
        <p className="bo-muted">
          Suivez l’activité de la plateforme et l’état des contenus pédagogiques.
        </p>
      </div>

      <div className="bo-stats-grid">
        {cards.map(([label, value]) => (
          <article className="bo-stat-card" key={label}>
            <strong>{value}</strong>
            <span>{label}</span>
          </article>
        ))}
      </div>

      <article className="bo-panel">
        <div className="bo-panel__header">
          <div>
            <h3>File éditoriale</h3>
            <p className="bo-muted">
              Les contenus en brouillon ne sont pas exposés au front-office.
            </p>
          </div>
          <span className="bo-kpi">{draftCount} brouillon(s)</span>
        </div>
      </article>
    </div>
  );
}

function BackofficeShell({ admin, onLogout }) {
  const [section, setSection] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isCompact, setIsCompact] = useState(isCompactViewport);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuButtonRef = useRef(null);
  const navigationRef = useRef(null);
  const [catalog, setCatalog] = useState(null);
  const [users, setUsers] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [globalError, setGlobalError] = useState('');
  const [notice, setNotice] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setGlobalError('');
    try {
      const canContent = hasPermission(admin, 'content:manage');
      const canUsers = hasPermission(admin, 'users:read');
      const [nextCatalog, nextUsers] = await Promise.all([
        canContent
          ? adminApi.catalog({
              includeCourses: false,
              includeResources: false,
              includeSimulations: false,
            })
          : Promise.resolve(null),
        canUsers
          ? adminApi.users({ limit: 1 })
          : Promise.resolve({ items: [], total: 0 }),
      ]);
      setCatalog(nextCatalog);
      setUsers(nextUsers || { items: [], total: 0 });
    } catch (error) {
      if (error.status === 401 || error.status === 403) {
        onLogout();
        return;
      }
      setGlobalError(error.message || 'Chargement du back-office impossible.');
    } finally {
      setLoading(false);
    }
  }, [admin, onLogout]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Mobile/tablet: the navigation is an off-canvas drawer, closed by
  // default. Desktop keeps its independently collapsible icon sidebar.
  useEffect(() => {
    const media = typeof window.matchMedia === 'function'
      ? window.matchMedia(MOBILE_MENU_QUERY)
      : null;
    const update = () => {
      const next = isCompactViewport();
      setIsCompact(next);
      setMobileMenuOpen(false);
    };
    if (media?.addEventListener) media.addEventListener('change', update);
    else if (media?.addListener) media.addListener(update);
    window.addEventListener('resize', update);
    return () => {
      if (media?.removeEventListener) media.removeEventListener('change', update);
      else if (media?.removeListener) media.removeListener(update);
      window.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    if (!isCompact || !mobileMenuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTarget = navigationRef.current?.querySelector('.bo-nav button.is-active')
      || navigationRef.current?.querySelector('.bo-nav button');
    focusTarget?.focus();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setMobileMenuOpen(false);
        mobileMenuButtonRef.current?.focus();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(navigationRef.current?.querySelectorAll(
        'button:not(:disabled), a[href]'
      ) || []);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !navigationRef.current.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !navigationRef.current.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isCompact, mobileMenuOpen]);

  const closeMobileNavigation = () => {
    setMobileMenuOpen(false);
    mobileMenuButtonRef.current?.focus();
  };

  // Keep subject options fresh after changes from another tab/operator.
  useEffect(() => {
    const syncOnFocus = () => refresh();
    const syncWhenVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', syncOnFocus);
    document.addEventListener('visibilitychange', syncWhenVisible);
    return () => {
      window.removeEventListener('focus', syncOnFocus);
      document.removeEventListener('visibilitychange', syncWhenVisible);
    };
  }, [refresh]);

  async function runAction(action, successMessage) {
    setGlobalError('');
    setNotice('');
    try {
      await action();
      if (successMessage) setNotice(successMessage);
      return true;
    } catch (error) {
      if (error.status === 401 || error.status === 403) {
        if (error.status === 401) onLogout();
        else setGlobalError(error.message || 'Action refusée.');
      } else {
        setGlobalError(error.message || 'Action impossible.');
      }
      return false;
    }
  }

  let content = null;
  if (section === 'dashboard') {
    content = <Dashboard catalog={catalog} users={users} />;
  } else if (section === 'modules') {
    content = (
      <ModulesManager onChanged={refresh} onUnauthorized={onLogout} />
    );
  } else if (section === 'quiz') {
    content = (
      <QuizManager catalog={catalog} refresh={refresh} runAction={runAction} onUnauthorized={onLogout} />
    );
  } else if (section === 'courses') {
    content = (
      <CourseManager
        catalog={catalog}
        refresh={refresh}
        runAction={runAction}
        onUnauthorized={onLogout}
      />
    );
  } else if (section === 'simulations') {
    content = (
      <SimulationManager refresh={refresh} onUnauthorized={onLogout} />
    );
  } else if (section === 'resources') {
    content = (
      <ResourceManager
        catalog={catalog}
        refresh={refresh}
        runAction={runAction}
        onUnauthorized={onLogout}
      />
    );
  } else if (section === 'users') {
    content = (
      <UserManagement
        permissions={admin.permissions || []}
        onUnauthorized={onLogout}
      />
    );
  } else if (section === 'billing') {
    content = <SubscriptionPlanManager onUnauthorized={onLogout} />;
  } else if (section === 'referrals') {
    content = <ReferralManager onUnauthorized={onLogout} />;
  } else if (section === 'operators') {
    content = (
      <AdminOperators
        currentAdmin={admin}
        onUnauthorized={onLogout}
      />
    );
  }

  const collapsedOnDesktop = sidebarCollapsed && !isCompact;
  const activeSectionLabel = NAV_ITEMS.find(([key]) => key === section)?.[1] || 'Navigation';

  return (
    <div className={[
      'bo-app',
      collapsedOnDesktop ? 'bo-app--sidebar-collapsed' : '',
      isCompact && mobileMenuOpen ? 'bo-app--mobile-menu-open' : '',
    ].filter(Boolean).join(' ')}>
      {isCompact && mobileMenuOpen && (
        <button type="button" className="bo-mobile-backdrop"
          aria-label="Fermer le menu" onClick={closeMobileNavigation} />
      )}
      <aside id="bo-navigation-sidebar"
        ref={navigationRef}
        aria-label="Menu d’administration"
        className={[
          'bo-sidebar',
          collapsedOnDesktop ? 'bo-sidebar--collapsed' : '',
          isCompact && mobileMenuOpen ? 'bo-sidebar--mobile-open' : '',
        ].filter(Boolean).join(' ')}>
        <div className="bo-sidebar__brand">
          <div className="bo-brand-mark bo-brand-mark--small">CB</div>
          <div className="bo-sidebar__brand-copy">
            <strong>CONCOURSBf</strong>
            <small>Espace de gestion</small>
          </div>
          <button
            type="button"
            className="bo-sidebar__refresh"
            onClick={refresh}
            disabled={loading}
            aria-label="Actualiser les données"
            title="Actualiser les données"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M20 11a8 8 0 0 0-14.9-4M4 4v4h4M4 13a8 8 0 0 0 14.9 4M20 20v-4h-4" />
            </svg>
          </button>
          <button
            type="button"
            className="bo-sidebar__toggle"
            onClick={() => setSidebarCollapsed((collapsed) => !collapsed)}
            aria-label={sidebarCollapsed ? 'Développer le menu latéral' : 'Réduire le menu latéral'}
            aria-expanded={!sidebarCollapsed}
            aria-controls="bo-navigation-sidebar"
            title={sidebarCollapsed ? 'Développer le menu' : 'Réduire le menu'}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="4" width="18" height="16" rx="2" />
              <path d="M9 4v16" />
              {sidebarCollapsed
                ? <path d="m13 10 3 2-3 2" />
                : <path d="m16 10-3 2 3 2" />}
            </svg>
          </button>
        </div>

        <nav className="bo-nav" aria-label="Navigation principale">
          {NAV_ITEMS
            .filter(([, , , permission]) => hasPermission(admin, permission))
            .map(([key, label]) => (
              <button
                key={key}
                className={section === key ? 'is-active' : ''}
                aria-current={section === key ? 'page' : undefined}
                aria-label={label}
                title={collapsedOnDesktop ? label : undefined}
                type="button"
                onClick={() => {
                  setNotice('');
                  setGlobalError('');
                  setSection(key);
                  if (isCompact) closeMobileNavigation();
                  if (hasPermission(admin, 'content:manage')) refresh();
                }}
              >
                <span className="bo-nav__icon"><NavIcon name={key} /></span>
                <span className="bo-nav__label">{label}</span>
              </button>
            ))}
        </nav>

        <div className="bo-sidebar__footer">
          <div className="bo-sidebar__user">
            <strong>{admin.pseudonym || admin.firstName || 'Admin'}</strong>
            <small>{admin.email}</small>
          </div>
          <button className="bo-button bo-button--ghost bo-sidebar__logout"
            type="button" onClick={onLogout} aria-label="Déconnexion"
            title={collapsedOnDesktop ? 'Déconnexion' : undefined}>
            <svg className="bo-sidebar__logout-icon" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true">
              <path d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4" />
              <path d="M14 7l5 5-5 5M9 12h10" />
            </svg>
            <span className="bo-sidebar__logout-label">Déconnexion</span>
          </button>
        </div>
      </aside>

      <header className="bo-mobile-header">
        <div className="bo-mobile-header__identity">
          <span className="bo-mobile-header__mark" aria-hidden="true">CB</span>
          <div>
            <strong>CONCOURSBf</strong>
            <span className="bo-mobile-header__section">{activeSectionLabel}</span>
          </div>
        </div>
        <button type="button" className="bo-mobile-menu-button"
          ref={mobileMenuButtonRef}
          aria-label={mobileMenuOpen ? 'Masquer les onglets' : 'Afficher les onglets'}
          aria-expanded={isCompact && mobileMenuOpen}
          aria-controls="bo-navigation-sidebar"
          onClick={() => setMobileMenuOpen((open) => !open)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {mobileMenuOpen
              ? <path d="M5 5l14 14M19 5 5 19" />
              : <path d="M4 6h16M4 12h16M4 18h16" />}
          </svg>
          <span>Menu</span>
        </button>
      </header>

      <main className="bo-main">
        {globalError && (
          <div className="bo-alert bo-alert--error">{globalError}</div>
        )}
        <StatusToast message={notice} />

        {loading && !catalog ? (
          <div className="bo-loading">Chargement du back-office…</div>
        ) : (
          content
        )}
      </main>
    </div>
  );
}

export default function BackofficeApp() {
  const [admin, setAdmin] = useState(null);
  const [checking, setChecking] = useState(true);
  const [inviteToken, setInviteToken] = useState(
    () => new URLSearchParams(window.location.search).get('inviteToken')
  );

  useEffect(() => {
    if (inviteToken) {
      setChecking(false);
      return;
    }
    adminApi
      .me()
      .then((result) => setAdmin(result.user))
      .catch(() => setAdmin(null))
      .finally(() => setChecking(false));
  }, [inviteToken]);

  const logout = useCallback(async () => {
    try {
      await adminApi.logout();
    } catch {
      // A locally cleared UI session is enough when the server session
      // is already invalid or unreachable.
    }
    setAdmin(null);
  }, []);

  if (checking) {
    return <div className="bo-loading bo-loading--full">Vérification de la session…</div>;
  }

  if (inviteToken) {
    return (
      <InvitationAcceptScreen
        token={inviteToken}
        onDone={() => setInviteToken(null)}
      />
    );
  }

  if (!admin) {
    return <LoginScreen onAuthenticated={setAdmin} />;
  }

  return <BackofficeShell admin={admin} onLogout={logout} />;
}
